import { encode } from '@s4l/utils/crypto/base64'
import { z } from 'zod'
import { Config } from '../_config'
import type { POSTBannerResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/banner'
import type { GETpropertiesByIDsResponse } from '../property/infrastructure/HTTPPropertyRepository/schemas/properties/ids'
import type { POSTRelatedResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/related'
import type { SearchProperty } from '../property/infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import { Model } from './architecture'
import { ImageBucket } from './ImageBucket'
import { ImageCategoryOrder } from './ImageCategoryOrder'
import { ImageCode } from './ImageCode'
import { ImageSize } from './ImageSize'
import { Path } from './Path'

export const ImageValidation = z.object({
  bucket: z.instanceof(ImageBucket, { message: 'bucket is required' }),
  path: z.instanceof(Path, { message: 'path is required' }),
  code: z.instanceof(ImageCode, { message: 'code is required' }),
  categoryOrder: z
    .instanceof(ImageCategoryOrder, { message: 'categoryOrder is required' })
    .optional(),

  size: z.instanceof(ImageSize, { message: '[Image] size is required' }),
})

export class Image extends Model {
  static create({ bucket, path, code, categoryOrder, size }: z.infer<typeof ImageValidation>) {
    ImageValidation.parse({ bucket, path, code, categoryOrder, size })
    return new Image(bucket, path, code, size, categoryOrder)
  }

  static fromAPI({
    json,
  }: {
    json: GETpropertiesByIDsResponse['properties'][number]['images'][number]
  }) {
    return Image.create({
      bucket: ImageBucket.create({ value: json.bucket }),
      path: Path.create({ value: json.path }),
      code: ImageCode.create({ value: json.code }),
      size: ImageSize.create({ width: json.width, height: json.height }),
      categoryOrder: ImageCategoryOrder.create({ value: json.categoryOrder }),
    })
  }

  static fromRelatedAPI({
    json,
  }: {
    json: POSTRelatedResponseType['properties'][number]['images'][number]
  }) {
    return Image.create({
      bucket: ImageBucket.create({ value: json.bucket }),
      path: Path.create({ value: json.path }),
      code: ImageCode.create({ value: json.code }),
      categoryOrder: ImageCategoryOrder.empty(),
      size: ImageSize.create({ width: json.width, height: json.height }),
    })
  }

  static fromBannerApi({
    json,
  }: {
    json: POSTBannerResponseType['properties'][number]['images'][number]
  }) {
    return Image.create({
      bucket: ImageBucket.create({ value: json.bucket }),
      path: Path.create({ value: json.path }),
      code: ImageCode.create({ value: json.code }),
      categoryOrder: ImageCategoryOrder.empty(),
      size: ImageSize.create({ width: json.width, height: json.height }),
    })
  }

  static fromSearchApiLegacy({ json }: { json: SearchProperty['photos'][number] }) {
    return Image.create({
      bucket: ImageBucket.create({ value: json.bucket }),
      path: Path.create({ value: json.path }),
      code: ImageCode.create({ value: json.code }),
      categoryOrder: ImageCategoryOrder.empty(),
      size: ImageSize.create({ width: json.width, height: json.height }),
    })
  }

  static fromJSON({ json }: { json: ReturnType<Image['toJSON']> }) {
    return Image.create({
      bucket: ImageBucket.create({ value: json.bucket }),
      path: Path.create({ value: json.path }),
      code: ImageCode.create({ value: json.code }),
      size: ImageSize.create(json.size),
      ...(json.categoryOrder && {
        categoryOrder: ImageCategoryOrder.create({ value: json.categoryOrder }),
      }),
    })
  }

  constructor(
    private readonly _bucket: ImageBucket,
    private readonly _path: Path,
    private readonly _code: ImageCode,
    private readonly _size: ImageSize,
    private readonly _categoryOrder?: ImageCategoryOrder
  ) {
    super()
  }

  get bucket() { return this._bucket.value } // prettier-ignore

  get path() { return this._path.value } // prettier-ignore

  get code() { return this._code.value } // prettier-ignore

  get categoryOrder() { return this._categoryOrder?.value } // prettier-ignore

  get size() { return this._size.toJSON() } // prettier-ignore

  toJSON() {
    return {
      bucket: this.bucket,
      path: this.path,
      code: this.code,
      size: this.size,
      ...(this.categoryOrder && { categoryOrder: this.categoryOrder }),
    }
  }

  toBase64(extra?: Record<string, unknown>) {
    return encode({
      bucket: this.bucket,
      key: this.path,
      ...(extra && { ...extra }),
    })
  }

  toUrl({ config, width }: { config: Config; width?: number }) {
    const edits = {
      toFormat: 'webp',
      resize: {
        width,
        fit: 'cover',
      },
    }

    return `${config.get('IMAGE_SERVICE')}/${this.toBase64(width ? { edits } : undefined)}`
  }
}
